<template>
  <div class="acImage" :style="reserveStyle">
    <div
      v-show="isLoading"
      class="acImage_shimmer"
      :class="`-${shimmerVariant}`"
      data-test="ac-image-shimmer"
    />

    <img
      v-if="mode === 'img'"
      ref="imgRef"
      class="acImage_img"
      :class="{ '-loaded': isReady }"
      :src="resolvedSrc"
      :alt="alt"
      :loading="loading"
      :decoding="decoding"
      :style="fitStyle"
      data-test="ac-image-img"
      @load="onLoad"
      @error="onError"
    />

    <div
      v-else
      class="acImage_bg"
      :class="{ '-loaded': isReady }"
      :style="bgStyle"
      data-test="ac-image-bg"
    />

    <slot v-if="state === 'error' && !isPlaceholder" name="error" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue'
import placeholderImage from '@web/ui-kit/images/placeholder.jpg'

type ImageMode = 'img' | 'background'
type ImageState = 'loading' | 'loaded' | 'error'
type ObjectFit = 'cover' | 'contain' | 'fill' | 'none'
type ShimmerVariant = 'sweep' | 'pulse' | 'wave'

const props = withDefaults(defineProps<{
  src: string
  alt?: string
  mode?: ImageMode
  aspectRatio?: string | number
  width?: string | number
  height?: string | number
  objectFit?: ObjectFit
  loading?: 'lazy' | 'eager'
  decoding?: 'sync' | 'async' | 'auto'
  radius?: string | number
  shimmerVariant?: ShimmerVariant
  placeholder?: string
}>(), {
  alt: '',
  mode: 'img',
  objectFit: 'cover',
  loading: 'lazy',
  decoding: 'auto',
  shimmerVariant: 'sweep',
  placeholder: placeholderImage
})

const emit = defineEmits<{
  load: []
  error: []
}>()

const slots = useSlots()

const imgRef = ref<HTMLImageElement>()
const state = ref<ImageState>('loading')

// Заглушку показываем, когда src пуст или картинка не загрузилась — но только если
// задан placeholder и не передан кастомный слот error (он имеет приоритет на ошибке).
const isPlaceholder = computed(() => {
  if (!props.placeholder) return false
  if (!props.src) return true
  return state.value === 'error' && !slots.error
})
const resolvedSrc = computed(() => isPlaceholder.value ? props.placeholder : props.src)

const isLoading = computed(() => state.value === 'loading' && !isPlaceholder.value)
const isReady = computed(() => state.value === 'loaded' || isPlaceholder.value)

const toCssSize = (v: string | number | undefined): string | undefined => {
  if (v === undefined) return undefined
  return typeof v === 'number' ? `${v}px` : v
}

const reserveStyle = computed(() => ({
  aspectRatio: props.aspectRatio !== undefined ? String(props.aspectRatio) : undefined,
  width: toCssSize(props.width),
  height: toCssSize(props.height),
  borderRadius: toCssSize(props.radius)
}))

const fitStyle = computed(() => (isPlaceholder.value ? null : { objectFit: props.objectFit }))

const bgStyle = computed(() => ({
  backgroundImage: `url("${resolvedSrc.value}")`,
  backgroundSize: props.objectFit === 'contain' ? 'contain' : 'cover'
}))

let preloader: HTMLImageElement | null = null

function cancelPreloader (): void {
  if (preloader) {
    preloader.onload = null
    preloader.onerror = null
    preloader = null
  }
}

function preloadBackground (): void {
  cancelPreloader()
  preloader = new Image()
  preloader.onload = () => { state.value = 'loaded'; emit('load'); cancelPreloader() }
  preloader.onerror = () => { state.value = 'error'; emit('error'); cancelPreloader() }
  preloader.src = props.src
}

function onLoad (): void {
  state.value = 'loaded'
  emit('load')
}

function onError (): void {
  state.value = 'error'
  emit('error')
}

watch(() => props.src, () => {
  state.value = 'loading'
  if (props.mode === 'background') preloadBackground()
})

onMounted(() => {
  if (props.mode === 'background') {
    preloadBackground()
    return
  }
  if (imgRef.value?.complete && imgRef.value.naturalWidth > 0) {
    state.value = 'loaded'
  }
})

onBeforeUnmount(cancelPreloader)

defineOptions({ name: 'AcImage' })
</script>

<style lang="scss" scoped>
.acImage {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;

  &_shimmer {
    --ac-shimmer-base: var(--ac-color-light-gray);
    --ac-shimmer-highlight: var(--ac-color-gray-substance);
    --ac-shimmer-speed: 3s;

    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: var(--ac-shimmer-base);

    &.-sweep {
      background-image: linear-gradient(
        90deg,
        var(--ac-shimmer-base) 0%,
        var(--ac-shimmer-highlight) 50%,
        var(--ac-shimmer-base) 100%
      );
      background-size: 200% 100%;
      animation: acImageShimmer var(--ac-shimmer-speed) ease-in-out infinite;
    }

    &.-pulse {
      animation: acImagePulse var(--ac-shimmer-speed) ease-in-out infinite;
    }

    &.-wave {
      animation: acImageWave var(--ac-shimmer-speed) ease-in-out infinite;
    }
  }

  &_img,
  &_bg {
    display: block;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity .3s ease;

    &.-loaded {
      opacity: 1;
    }
  }

  &_bg {
    background-position: center;
    background-repeat: no-repeat;
  }
}

@keyframes acImageShimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes acImagePulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: .4; }
}

@keyframes acImageWave {
  0%, 100% { background-color: var(--ac-shimmer-base); }
  50%      { background-color: var(--ac-shimmer-highlight); }
}

@media (prefers-reduced-motion: reduce) {
  .acImage_shimmer {
    &.-sweep,
    &.-pulse,
    &.-wave {
      animation: none;
    }
  }
}
</style>
